import dayjs from '../../utils/dayjs';
import {
  BlockStack,
  Box,
  Button,
  ChoiceList,
  Collapsible,
  Divider,
  Icon,
  InlineStack,
  Link,
  ProgressBar,
  SkeletonBodyText,
  Text,
} from '@shopify/polaris';
import For from 'components/For';
import { AppExtensionIcon, CartUpIcon, CashDollarIcon, FilterIcon, PlusIcon, RefreshIcon, RewardIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import helpers from 'helpers/index';
import { TypedTripData, useGetTripsByJourney } from 'queries/trip.query';
import 'media/css/journey-list.scss';
import TripTimeModal from './tripTimeModal';
import useDidMountEffect from 'components/useDidMountEffect';
import { getTripStatusBadge2 } from './components/tripStatusBadge';
import Capacity from 'components/Capacity';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruckFast } from '@fortawesome/free-solid-svg-icons';

export default function TripTimeColumn({
  onHourChange,
  currentJourneyID,
  currentTripSelectedDataCallback,
  currentSelectedDates,
}: {
  onHourChange: (hour: string) => void;
  currentJourneyID: string | bigint;
  currentTripSelectedDataCallback: (data: TypedTripData | undefined) => void;
  currentSelectedDates: string /** YYYY-MM-DD */;
}) {
  // ---------- STATE ----------
  const [selectedTime, setSelectedTime] = useState<string>(''); // trip id (string)

  useEffect(() => {
    setSelectedTime('');
  }, [currentSelectedDates]);

  // ⛳️ Thay cho "init 1 lần": lắng nghe URL mỗi khi location.search đổi
  useEffect(() => {
    const q = helpers.ExtractUrl(window.location.search) || {};
    const h = String(q.h ?? '');

    // chỉ set khi khác để tránh setState dư thừa + không gây loop
    setSelectedTime((prev) => (prev !== h ? h : prev));
  }, []);

  const { data: tripTotalData, isLoading, isSuccess } = useGetTripsByJourney(currentJourneyID, new Date(currentSelectedDates || Date.now()));

  const [tripData, setTripData] = useState<TypedTripData[]>([]);
  const [tripMetaData, setTripMetaData] = useState<{
    id: bigint;
    journey_id: bigint;
    departure_date: string;
  }>(null);

  useEffect(() => {
    if (tripTotalData) {
      const { data, meta } = tripTotalData;
      setTripData(data || []);
      setTripMetaData(meta || null);
    }
  }, [tripTotalData, isSuccess]);

  // Cập nhật trip đã chọn (từ id string)
  useEffect(() => {
    if (!tripTotalData || !selectedTime) return;
    const trip = tripTotalData.data.find((el: TypedTripData) => String(el.id ?? '') === String(selectedTime));
    currentTripSelectedDataCallback(trip);
  }, [tripTotalData, selectedTime, currentTripSelectedDataCallback]);

  // Hôm nay?
  const isToday = useMemo(() => {
    let td = dayjs(new Date()).format('DD-MM-YYYY');
    let othrD = dayjs(new Date(currentSelectedDates)).format('DD-MM-YYYY');
    return td === othrD;
  }, [currentSelectedDates]);

  // Chia past/upcoming nếu là hôm nay
  const { pastTrips, upcomingTrips } = useMemo(() => {
    if (!isToday) return { pastTrips: [] as TypedTripData[], upcomingTrips: tripData };
    const now = new Date();
    const todayDate = new Date(currentSelectedDates);
    const past: TypedTripData[] = [];
    const upcoming: TypedTripData[] = [];

    (tripData || []).forEach((t) => {
      const [h, m] = (t.time ?? '00:00').split(':').map((n) => Number(n) || 0);
      const tripDate = new Date(todayDate);
      tripDate.setHours(h, m, 0, 0);
      if (tripDate < now) past.push(t);
      else upcoming.push(t);
    });

    past.sort((a, b) => (a.time ?? '').localeCompare(b.time ?? ''));
    upcoming.sort((a, b) => (a.time ?? '').localeCompare(b.time ?? ''));

    return { pastTrips: past, upcomingTrips: upcoming };
  }, [tripData, isToday, currentSelectedDates]);

  // Tránh spam callback nếu giá trị không đổi
  const lastSentRef = useRef<{ h?: string }>({});

  const previousJourneyIdRef = useRef<string | bigint | undefined>();

  useEffect(() => {
    // Đừng reset nếu đang khởi tạo từ URL (lần render đầu tiên)
    if (previousJourneyIdRef.current === undefined) {
      previousJourneyIdRef.current = currentJourneyID;
      return;
    }

    if (previousJourneyIdRef.current !== currentJourneyID) {
      setSelectedTime('');
      lastSentRef.current = {};
      currentTripSelectedDataCallback(undefined);
      previousJourneyIdRef.current = currentJourneyID;
    }
  }, [currentJourneyID, currentTripSelectedDataCallback]);

  useDidMountEffect(() => {
    if (selectedTime !== '' && lastSentRef.current.h !== selectedTime) {
      lastSentRef.current.h = selectedTime;
      onHourChange(selectedTime);
    }
  }, [selectedTime]);

  const getDriverRole = (driver_role: string | undefined) => {
    switch (driver_role) {
      case 'driver':
        return (
          <Text as="span" tone="subdued">
            c
          </Text>
        );
      case 'assistant':
        return (
          <Text as="span" tone="subdued">
            p
          </Text>
        );
      default:
        return null;
    }
  };

  /** Modal */
  const [tripTimeShowModal, setTripTimeShowModal] = useState(false);

  const sortedDrivers = useCallback((drivers: any[]) => {
    if (!drivers) return [];
    return [...drivers].sort((driverA, driverB) => {
      const roleScore = (driver: any) => (driver?.driver_role === 'driver' ? 0 : 1);
      return roleScore(driverA) - roleScore(driverB);
    });
  }, []);

  // Manifest (object) – CHỈ HIỂN THỊ, KHÔNG CLICK
  const renderTripManifestObject = useCallback((timeSlot: TypedTripData) => {
    const { trip_manifest } = timeSlot;
    if (!trip_manifest)
      return (
        <Text as="p" variant="bodySm">
          Chưa có xe nào
        </Text>
      );

    const manifestId = String(trip_manifest.id ?? '');
    const seatingName = trip_manifest?.seating?.seating_name ?? '---';
    // const totalSeats = trip_manifest?.seating?.seating_number ?? 0;
    // const occupiedSeats = trip_manifest?.total_occupied_seats ?? 0;

    return (
      <BlockStack key={`mn_${manifestId}`} gap="200">
        <BlockStack gap="0">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="200">
              {/* <SeatIndicator size={8} totalSeats={totalSeats} occupiedSeats={occupiedSeats} /> */}
              <Text as="p">{getTripStatusBadge2(trip_manifest?.trip_status)}</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                <span>{seatingName}</span>
              </Text>
            </BlockStack>
            {trip_manifest?.vehicle?.license_plate ? <strong>{String(trip_manifest?.vehicle?.license_plate).toUpperCase()}</strong> : null}
          </InlineStack>
          <div>
            {sortedDrivers(trip_manifest?.trip_manifest_driver)?.map((drv, iDrv) => (
              <Text as="p" fontWeight="bold" variant="bodySm" key={`drv_${manifestId}_${iDrv}`}>
                {drv.driver_role === 'driver' ? (
                  <>
                    <sup>{getDriverRole(drv.driver_role)}</sup> {drv?.driver_data?.display_name}
                  </>
                ) : null}
              </Text>
            ))}
          </div>
        </BlockStack>
      </BlockStack>
    );
  }, []);

  // Chọn timeslot
  const onSelectTimeslot = useCallback((slot: TypedTripData) => {
    setSelectedTime(String(slot.id ?? ''));
  }, []);

  const normalizePlate = (plate?: string) => (plate || '').trim().toUpperCase();

  // ⛳️ 3) STATE cho lọc tài xế
  const [selectedDriverNames, setSelectedDriverNames] = useState<string[]>([]);
  const [selectedVehiclePlates, setSelectedVehiclePlates] = useState<string[]>([]);

  // ⛳️ 4) Gom tất cả tên tài xế từ dữ liệu (tripData)
  const allDriverNames: string[] = useMemo(() => {
    const names = new Set<string>();
    for (const t of tripData || []) {
      // trip_manifest có thể là object hoặc array => chuẩn hoá
      const manifests = Array<any>(t?.trip_manifest as any);
      for (const mf of manifests) {
        for (const d of mf?.trip_manifest_driver || []) {
          const name = (d?.driver_data?.display_name || '').trim();
          if (name) names.add(name);
        }
      }
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [tripData]);

  const driverChoices = useMemo(() => allDriverNames.map((n) => ({ label: n, value: n })), [allDriverNames]);

  const allVehiclePlates: string[] = useMemo(() => {
    const plates = new Set<string>();
    for (const t of tripData || []) {
      const manifests = Array<any>(t?.trip_manifest as any);
      for (const mf of manifests) {
        const plate = normalizePlate(mf?.vehicle?.license_plate);
        if (plate) plates.add(plate);
      }
    }
    return Array.from(plates).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [tripData]);

  const vehicleChoices = useMemo(() => allVehiclePlates.map((p) => ({ label: p, value: p })), [allVehiclePlates]);

  // ⛳️ 5) Check manifest có khớp bộ lọc tài xế không
  const manifestMatchesDriverFilter = useCallback(
    (mf: any) => {
      if (!selectedDriverNames.length) return true;
      const names = (mf?.trip_manifest_driver || []).map((d: any) => (d?.driver_data?.display_name || '').trim());
      return names.some((n: string) => selectedDriverNames.includes(n));
    },
    [selectedDriverNames]
  );

  const manifestMatchesVehicleFilter = useCallback(
    (mf: any) => {
      if (!selectedVehiclePlates.length) return true;
      const plate = normalizePlate(mf?.vehicle?.license_plate);
      return plate ? selectedVehiclePlates.includes(plate) : false;
    },
    [selectedVehiclePlates]
  );

  // Render list trips
  const renderTrips = useCallback(
    (trips: TypedTripData[]) => (
      <div className="trip_time_element trip_time_element_s_down">
        <For each={trips} fallback={<Text as="p">Không có chuyến nào</Text>}>
          {(timeSlot: TypedTripData, index) => {
            const isActive = selectedTime && selectedTime === String(timeSlot.id ?? '');
            // SAFE percentage
            const totalSeat = timeSlot?.trip_manifest?.seating?.seating_available_count ?? 0;

            const totalBookedSeat = timeSlot?.current_ticket_count ?? 0;
            const isOverbooked = totalSeat > 0 && totalBookedSeat > totalSeat;
            const rawPct = totalSeat > 0 ? (totalBookedSeat / totalSeat) * 100 : 0;
            const percentage = Math.max(0, Math.min(100, Math.floor(rawPct)));

            // NEW: Tính “dim” theo bộ lọc tài xế
            const manifests = Array<any>(timeSlot?.trip_manifest as any);
            const hasAnyMatch = manifests.some((mf) => manifestMatchesDriverFilter(mf) && manifestMatchesVehicleFilter(mf));
            const shouldDim = (selectedDriverNames.length > 0 || selectedVehiclePlates.length > 0) && !hasAnyMatch;

            /** tính số % xuất hóa đơn */
            const totalOrders = timeSlot?.trip_manifest?.orders.length || 0;
            const totalInvoiced = timeSlot?.trip_manifest?.orders.filter((o) => o?.invoices).length || 0;
            const invoicePercentage = totalOrders > 0 ? (totalInvoiced / totalOrders) * 100 : 0;

            return (
              <a
                href={`#timeslots___${String(timeSlot.id ?? index)}`}
                key={`timeslots___${String(timeSlot.id ?? index)}`}
                onClick={(e) => {
                  e.preventDefault();
                  onSelectTimeslot(timeSlot);
                }}
                className={`link-card timeslot_element ${isActive ? 'active' : ''} ${shouldDim ? 'is-dimmed' : ''} `}
                role="button"
                tabIndex={0}
                aria-pressed={!!isActive}
                title="Chọn giờ này"
              >
                {timeSlot?.trip_manifest?.bonus_amount > 0 ? (
                  <span className="timeslot_progress_bar____icon_bonus">
                    <Icon source={RewardIcon} tone="success" />
                  </span>
                ) : null}
                {/* progress bar không ăn click */}
                <div className="timeslot_progress_bar" aria-hidden style={{ pointerEvents: 'none' }}>
                  <div
                    className={`timeslot_progress_bar__inner ${isOverbooked ? 'overbooking' : percentage >= 90 ? '' : 'notice'}`}
                    style={{ width: `${percentage}%`, pointerEvents: 'none' }}
                  />
                </div>

                <Box zIndex="10" position="relative">
                  <BlockStack gap="200">
                    <InlineStack gap="200" align="space-between" blockAlign="center">
                      <Text as="span" tone="critical" fontWeight="bold">
                        <InlineStack align="start" blockAlign="center" gap="100">
                          <span>{timeSlot.time ?? '--:--'} </span>
                          {timeSlot?.trip_manifest?.total_occupied_good > 0 ? (
                            <span className="small-icon">
                              <FontAwesomeIcon size="xs" icon={faTruckFast} />
                            </span>
                          ) : null}
                        </InlineStack>
                      </Text>
                      <Text as="span" tone="subdued" variant="bodyXs">{`${totalBookedSeat}/${totalSeat}`}</Text>
                    </InlineStack>

                    <BlockStack gap="200">{renderTripManifestObject(timeSlot)}</BlockStack>
                  </BlockStack>
                </Box>
                <div className="timeslot_progress_bar__invoice_couting" aria-hidden>
                  <ProgressBar progress={invoicePercentage} size="small" tone={invoicePercentage > 99 ? 'success' : 'critical'} />
                </div>
              </a>
            );
          }}
        </For>
      </div>
    ),
    [
      selectedTime,
      selectedDriverNames.length,
      selectedVehiclePlates.length,
      renderTripManifestObject,
      manifestMatchesDriverFilter,
      manifestMatchesVehicleFilter,
      onSelectTimeslot,
    ]
  );

  /** Mở bộ lọc */
  const [openDriverFilter, setOpenDriverFilter] = useState(false);
  const [openVehicleFilter, setOpenVehicleFilter] = useState(false);
  const handleToggleDriverFilter = useCallback(() => setOpenDriverFilter((open) => !open), []);
  const handleToggleVehicleFilter = useCallback(() => setOpenVehicleFilter((open) => !open), []);

  return (
    <>
      {/* ⛳️ 7) Khối UI bộ lọc tài xế (đặt trên danh sách trip) */}
      <InlineStack align="space-between" blockAlign="center">
        <Text as="span" fontWeight="bold">
          Ngày {dayjs(new Date(currentSelectedDates)).format('DD-MM-YYYY')}
        </Text>
        <InlineStack gap="400" align="space-between" blockAlign="end">
          <Link onClick={handleToggleDriverFilter}>Lọc tài xế</Link>
          <Link onClick={handleToggleVehicleFilter}>Lọc biển số xe</Link>
          <Icon source={FilterIcon} />
        </InlineStack>
      </InlineStack>
      <br />
      <Collapsible open={openDriverFilter} id="basic-collapsible" transition={{ duration: '100ms', timingFunction: 'ease-in-out' }} expandOnPrint>
        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
          <BlockStack gap="200">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingSm">
                Lọc theo tài xế
              </Text>
              <Button
                onClick={() => setSelectedDriverNames([])}
                size="micro"
                disabled={!selectedDriverNames.length}
                accessibilityLabel="Clear driver filter"
              >
                Xóa lọc
              </Button>
            </InlineStack>

            {driverChoices.length ? (
              <ChoiceList title="" allowMultiple selected={selectedDriverNames} onChange={(v) => setSelectedDriverNames(v)} choices={driverChoices} />
            ) : (
              <Text as="p" tone="subdued" variant="bodySm">
                Không có tài xế nào trong dữ liệu hiện tại.
              </Text>
            )}
          </BlockStack>
        </Box>
      </Collapsible>
      <Collapsible
        open={openVehicleFilter}
        id="vehicle-filter-collapsible"
        transition={{ duration: '100ms', timingFunction: 'ease-in-out' }}
        expandOnPrint
      >
        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
          <BlockStack gap="200">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingSm">
                Lọc theo biển số xe
              </Text>
              <Button
                onClick={() => setSelectedVehiclePlates([])}
                size="micro"
                disabled={!selectedVehiclePlates.length}
                accessibilityLabel="Clear vehicle filter"
              >
                Xóa lọc
              </Button>
            </InlineStack>

            {vehicleChoices.length ? (
              <ChoiceList
                title=""
                allowMultiple
                selected={selectedVehiclePlates}
                onChange={(v) => setSelectedVehiclePlates(v)}
                choices={vehicleChoices}
              />
            ) : (
              <Text as="p" tone="subdued" variant="bodySm">
                Không có xe nào trong dữ liệu hiện tại.
              </Text>
            )}
          </BlockStack>
        </Box>
      </Collapsible>
      <BlockStack gap="400">
        {isLoading ? (
          <SkeletonBodyText />
        ) : (
          <>
            {isToday ? (
              <>
                <Text as="h3" variant="headingMd" tone="subdued">
                  Chưa tới giờ khởi hành
                </Text>
                {renderTrips(upcomingTrips)}

                <Divider />

                <Text as="h3" variant="headingMd" tone="subdued">
                  Đã khởi hành
                </Text>
                {renderTrips(pastTrips)}
              </>
            ) : (
              renderTrips(tripData || [])
            )}
          </>
        )}
      </BlockStack>

      <br />
      <Divider borderWidth="100" />
      <br />
      <Capacity current_user_can={'manage_trip_manifest'}>
        <div key={`last_time_element`} className="clickable">
          <InlineStack align="space-between" blockAlign="center">
            <Button icon={PlusIcon} disabled={!tripMetaData?.departure_date} variant="plain" onClick={() => setTripTimeShowModal(true)}>
              Thêm giờ chạy
            </Button>
          </InlineStack>
        </div>
      </Capacity>
      <br />
      <Divider borderWidth="100" />
      <br />
      <TripTimeModal
        onClose={(output) => {
          if (output) {
            setSelectedTime(String(output.id ?? ''));
            // setSelectedTimeInText(String(output.time ?? ''));
          }
          setTripTimeShowModal(false);
        }}
        show={tripTimeShowModal}
        current_journey_id={currentJourneyID}
        current_departure_date={new Date(currentSelectedDates || Date.now())}
      />
    </>
  );
}
